import React, { useEffect, useState, useMemo } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import { apiService } from '../services/apiService';

const mimeIcon = mime => {
  if (!mime) return '📄';
  if (mime.includes('pdf'))   return '📕';
  if (mime.includes('word') || mime.includes('document')) return '📘';
  if (mime.includes('presentation') || mime.includes('powerpoint')) return '📙';
  if (mime.includes('sheet') || mime.includes('excel')) return '📗';
  if (mime.includes('image')) return '🖼️';
  if (mime.includes('video')) return '🎬';
  if (mime.includes('zip'))   return '🗜️';
  return '📄';
};

const MyMaterials = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');
  const [filterModule, setFilterModule] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await apiService.getAll('materials');
        setMaterials(data || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load materials.');
      } finally { setLoading(false); }
    };
    load();
  }, []);

  const modules = useMemo(() =>
    [...new Map(materials.filter(m => (m.moduleName || m.subjectName)).map(m => [m.moduleId || m.subjectId, m.moduleName || m.subjectName])).entries()]
      .map(([id, name]) => ({ id, name })),
    [materials]);

  const filtered = useMemo(() =>
    materials.filter(m => {
      const matchSearch  = [m.title, m.className, m.moduleName || m.subjectName, m.topic, m.semester].join(' ').toLowerCase().includes(search.toLowerCase());
      const matchModule = filterModule ? String(m.moduleId || m.subjectId) === filterModule : true;
      return matchSearch && matchModule;
    }), [materials, search, filterModule]);

  const formatSize = bytes => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleDownload = async (m) => {
    try {
      const response = await apiService.raw.get(`materials/${m.id}/download`, { responseType: 'blob' });
      const url  = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', m.originalName || m.fileName || 'material');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Download failed. Please try again.');
    }
  };

  if (loading) return <LoadingSpinner message="Loading materials..." />;

  return (
    <div className="page-container">
      <div className="page-header"><h1>My Learning Materials</h1></div>

      {error && <div className="error-message">{error}</div>}

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <input style={{ flex: 1, minWidth: 200 }} type="text" placeholder="Search materials..." value={search} onChange={e => setSearch(e.target.value)} />
        <select value={filterModule} onChange={e => setFilterModule(e.target.value)} style={{ minWidth: 160 }}>
          <option value="">All Modules</option>
          {modules.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="no-data-message">No materials available for your class yet.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
          {filtered.map(m => (
            <div key={m.id} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '1.25rem' }}>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '2rem', flexShrink: 0 }}>{mimeIcon(m.mimeType)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.25rem', wordBreak: 'break-word' }}>{m.title}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginBottom: '0.5rem' }}>
                    {m.className}{(m.moduleName || m.subjectName) && ` · ${m.moduleName || m.subjectName}`}
                    {m.topic && ` · ${m.topic}`}
                    {m.semester && ` · ${m.semester}`}
                  </div>
                  {m.description && <p style={{ fontSize: '0.85rem', margin: '0 0 0.75rem', color: 'var(--text-light)' }}>{m.description}</p>}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>
                      {formatSize(m.fileSize)} · {new Date(m.createdAt).toLocaleDateString()}
                    </span>
                    <button
                      onClick={() => handleDownload(m)}
                      className="btn btn-primary"
                      style={{ fontSize: '0.82rem', padding: '0.3rem 0.8rem' }}
                    >
                      ⬇ Download
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyMaterials;
